import type { QueryClient } from '@tanstack/react-query';

import { configureApiClient } from '@/shared/api/api-client';

import { refreshSession } from './auth.api';
import { useAuthStore } from '../store/auth.store';

/**
 * Connect the auth feature to the HTTP client.
 *
 * Called once from `main.tsx`, before React renders. Registration is not a
 * rendering concern, so it happens here, explicitly, at startup rather than
 * inside a component.
 *
 * The callbacks read the store through `getState()` rather than closing over a
 * value — they must see the *current* session on every request, not the one
 * that existed when this ran.
 *
 * `queryClient` is passed in (rather than imported from the app layer) so the
 * feature does not depend on the application shell; it is used to drop every
 * cached response when a session ends, so nothing from the previous session
 * can render for whoever signs in next.
 */
export function configureAuth(queryClient: QueryClient): void {
  configureApiClient({
    getAccessToken: () => useAuthStore.getState().session?.accessToken ?? null,

    refreshTokens: async () => {
      const session = useAuthStore.getState().session;
      if (!session) return null;

      try {
        const refreshed = await refreshSession(session.accessToken, session.refreshToken);
        useAuthStore.getState().setSession(refreshed);
        return refreshed.accessToken;
      } catch {
        // Refresh token rejected or already rotated by another tab, or the
        // account was deactivated or deleted. Either way the session is over.
        return null;
      }
    },

    onAuthFailure: () => {
      // Only end a session that exists — a stray 401 after sign-out must not
      // replace "signed out" with "your session expired".
      if (useAuthStore.getState().status !== 'authenticated') return;

      useAuthStore.getState().clear('expired');
      queryClient.clear();
    },
  });
}
